import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { EnvConfigLoaderService } from '../../shared/services/app';
import { SILENT_HTTP_ERRORS } from '../../shared/interceptors/server-http.interceptor';

/** The signed-in user's External ID account, as `GET /ExternalIdMigration/MyAccount` reports it (ZEY-3140). */
export interface MyExternalIdAccount {
  /** False when External ID isn't configured or the tenant is excluded: no password can be set here. */
  available: boolean;
  /** Null when External ID couldn't answer. */
  hasAccount: boolean | null;
  signInEmail: string | null;
  createdAt: string | null;
  passwordChangedAt: string | null;
  /** `YYYY-MM-DD` the password must be set by; null = no countdown. */
  deadline: string | null;
}

/**
 * The forced password change of the move to External ID (ZEY-3140). The API decides who is asked; the prompt shows
 * its own errors, so neither call raises the global error toasts.
 */
@Injectable({ providedIn: 'root' })
export class PasswordPromptService {
  constructor(private http: HttpClient, private env: EnvConfigLoaderService) {}

  private get base(): string {
    return `${this.env.getSettings('env')?.erpApiUrl}/ExternalIdMigration`;
  }

  private get silent(): HttpContext {
    return new HttpContext().set(SILENT_HTTP_ERRORS, true);
  }

  /** True when the signed-in user must choose a new password. A failed check never blocks anyone. */
  isRequired(): Observable<boolean> {
    return this.http.get<{ required: boolean }>(`${this.base}/PasswordStatus`, { context: this.silent }).pipe(
      map((result) => !!result?.required),
      catchError(() => of(false))
    );
  }

  /** For the /change-password page. Any failure reads as "couldn't check right now" (hasAccount null), never an error. */
  myAccount(): Observable<MyExternalIdAccount> {
    const unknown: MyExternalIdAccount = {
      available: true,
      hasAccount: null,
      signInEmail: null,
      createdAt: null,
      passwordChangedAt: null,
      deadline: null
    };
    return this.http.get<Partial<MyExternalIdAccount>>(`${this.base}/MyAccount`, { context: this.silent }).pipe(
      map((result) => ({ ...unknown, ...result, available: result?.available !== false, hasAccount: result?.hasAccount ?? null })),
      catchError(() => of(unknown))
    );
  }

  setPassword(newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.base}/SetPassword`, { newPassword }, { context: this.silent });
  }
}
